import { FormEvent, useEffect, useState } from 'react';
import {
  DISPOSITION_DOCS_NOT_ENOUGH,
  DISPOSITION_OTHER,
  MARK_AS_CANCEL_LABEL,
  MARK_AS_CLOSE_LABEL,
  MARK_AS_COMMENT_LABEL,
  MARK_AS_COMMENT_PLACEHOLDER,
  MARK_AS_DISPOSITION_LABEL,
  MARK_AS_REQUIRED_LABEL,
  MARK_AS_SAVE_LABEL,
  MARK_AS_SELECT_LABEL,
  MARK_AS_SOLVED_OPTION,
  MARK_AS_TITLE,
} from '../constants';
import type { IdvMarkAsPayload } from '../types';
import {
  ActionButton,
  CloseButton,
  CommentTextarea,
  DispositionSelect,
  FieldRow,
  Floater,
  FloaterActions,
  FloaterBackdrop,
  FloaterField,
  FloaterHead,
  FloaterTitle,
  RequiredLabel,
} from './styled';
import type { MarkAsFloaterProps } from './types';

type SelectedDisposition = IdvMarkAsPayload['disposition'] | '';

const MarkAsFloater = ({ attempt, saving = false, onCancel, onSubmit }: MarkAsFloaterProps) => {
  const [disposition, setDisposition] = useState<SelectedDisposition>('');
  const [comment, setComment] = useState('');
  const valid = disposition !== '' && comment.trim().length > 0;
  const titleId = `idv-mark-as-title-${attempt.id}`;

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) onCancel();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onCancel, saving]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!valid || !disposition || saving) return;
    await onSubmit({
      attempt_id: attempt.id,
      disposition,
      comment: comment.trim(),
    });
  };

  return (
    <>
      <FloaterBackdrop
        role="presentation"
        data-testid="idv-mark-as-backdrop"
        onMouseDown={() => {
          if (!saving) onCancel();
        }}
      />
      <Floater
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={saving}
        data-testid="idv-mark-as-floater"
        onSubmit={submit}
      >
        <FloaterHead>
          <FloaterTitle id={titleId}>{MARK_AS_TITLE(attempt.id)}</FloaterTitle>
          <CloseButton type="button" aria-label={MARK_AS_CLOSE_LABEL} disabled={saving} onClick={onCancel}>
            ×
          </CloseButton>
        </FloaterHead>

        <FloaterField>
          {MARK_AS_DISPOSITION_LABEL}
          <DispositionSelect
            autoFocus
            aria-label={MARK_AS_DISPOSITION_LABEL}
            value={disposition}
            disabled={saving}
            onChange={(event) => setDisposition(event.target.value as SelectedDisposition)}
          >
            <option value="">{MARK_AS_SELECT_LABEL}</option>
            <option value="solved">{MARK_AS_SOLVED_OPTION}</option>
            <option value="docs_not_enough">{DISPOSITION_DOCS_NOT_ENOUGH}</option>
            <option value="other">{DISPOSITION_OTHER}</option>
          </DispositionSelect>
        </FloaterField>

        <FloaterField>
          <FieldRow>
            <span>{MARK_AS_COMMENT_LABEL}</span>
            <RequiredLabel>{MARK_AS_REQUIRED_LABEL}</RequiredLabel>
          </FieldRow>
          <CommentTextarea
            aria-label={MARK_AS_COMMENT_LABEL}
            value={comment}
            placeholder={MARK_AS_COMMENT_PLACEHOLDER}
            disabled={saving}
            onChange={(event) => setComment(event.target.value)}
          />
        </FloaterField>

        <FloaterActions>
          <ActionButton type="button" disabled={saving} onClick={onCancel}>
            {MARK_AS_CANCEL_LABEL}
          </ActionButton>
          <ActionButton type="submit" primary disabled={!valid || saving}>
            {MARK_AS_SAVE_LABEL}
          </ActionButton>
        </FloaterActions>
      </Floater>
    </>
  );
};

export default MarkAsFloater;
